import { useEffect, useState, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '@/lib/api';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  ArrowLeft, Loader2, Send, Paperclip, FileText, Trash2, User, Calendar,
  CheckCircle2, Play, Archive, ListTodo,
} from 'lucide-react';

const STATUS_OPTIONS = [
  { value: 'todo', label: 'À faire' },
  { value: 'in_progress', label: 'En cours' },
  { value: 'done', label: 'Terminé' },
  { value: 'archived', label: 'Archivée' },
];
const PRIORITY_OPTIONS = [
  { value: 'low', label: 'Basse' },
  { value: 'medium', label: 'Moyenne' },
  { value: 'high', label: 'Haute' },
  { value: 'urgent', label: 'Urgente' },
];

export default function TaskDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const fileRef = useRef(null);
  const cmtFileRefs = useRef({});

  const [task, setTask] = useState(null);
  const [comments, setComments] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [savingField, setSavingField] = useState(null);
  const [newCmt, setNewCmt] = useState('');
  const [posting, setPosting] = useState(false);
  const [uploading, setUploading] = useState(false);

  useEffect(() => { loadAll(); loadUsers(); /* eslint-disable-next-line */ }, [id]);

  const loadAll = async () => {
    setLoading(true);
    try {
      const r = await api.get(`/tasks/${id}`);
      setTask(r.data?.task);
      setComments(r.data?.comments || []);
      document.title = (r.data?.task?.title || 'Tâche') + ' - Reno Resolve';
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Tâche introuvable');
      navigate('/taches');
    }
    setLoading(false);
  };
  const loadUsers = async () => {
    try {
      const r = await api.get('/users/list-slim');
      setUsers(r.data?.users || []);
    } catch (_) {}
  };

  const updateField = async (field, value) => {
    setSavingField(field);
    try {
      const r = await api.patch(`/tasks/${id}`, { [field]: value });
      setTask(r.data);
      toast.success('Tâche mise à jour');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erreur lors de la mise à jour');
    }
    setSavingField(null);
  };

  const handleDelete = async () => {
    if (!window.confirm('Supprimer définitivement cette tâche et tous ses commentaires ?')) return;
    try {
      await api.delete(`/tasks/${id}`);
      toast.success('Tâche supprimée');
      navigate('/taches');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erreur lors de la suppression');
    }
  };

  const postComment = async () => {
    if (!newCmt.trim()) return;
    setPosting(true);
    try {
      const r = await api.post(`/tasks/${id}/comments`, { body: newCmt.trim() });
      setComments((c) => [...c, r.data]);
      setNewCmt('');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erreur lors de l\'envoi');
    }
    setPosting(false);
  };

  const uploadTaskFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', f);
      const r = await api.post(`/tasks/${id}/attachments`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setTask((t) => ({ ...t, attachments: [...(t.attachments || []), r.data] }));
      toast.success('Fichier ajouté');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Erreur upload');
    }
    if (fileRef.current) fileRef.current.value = '';
    setUploading(false);
  };

  const uploadCmtFile = async (commentId, e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    try {
      const fd = new FormData();
      fd.append('file', f);
      const r = await api.post(`/tasks/comments/${commentId}/attachments`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setComments((cs) => cs.map((c) =>
        c.comment_id === commentId
          ? { ...c, attachments: [...(c.attachments || []), r.data] }
          : c
      ));
      toast.success('Fichier ajouté');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Erreur upload');
    }
    if (cmtFileRefs.current[commentId]) cmtFileRefs.current[commentId].value = '';
  };

  if (loading) {
    return (
      <div className="p-12 text-center" data-testid="task-loading">
        <Loader2 className="w-8 h-8 animate-spin mx-auto text-blue-600" />
      </div>
    );
  }
  if (!task) return null;

  const fullUrl = (u) => {
    if (!u) return '#';
    if (u.startsWith('http')) return u;
    return (process.env.REACT_APP_BACKEND_URL || '') + u;
  };

  return (
    <div className="space-y-6 max-w-4xl" data-testid="task-detail">
      <Button variant="ghost" onClick={() => navigate('/taches')} data-testid="task-back-btn">
        <ArrowLeft className="w-4 h-4 mr-1.5" /> Retour aux tâches
      </Button>

      <div className="bg-white rounded-xl border border-slate-200 p-6 space-y-5">
        <div className="flex items-start justify-between gap-3">
          <h1 className="text-2xl font-bold text-slate-900 flex-1" style={{ fontFamily: 'Manrope, sans-serif' }}>
            {task.title}
          </h1>
          <Button variant="ghost" size="sm" onClick={handleDelete} className="text-red-600 hover:bg-red-50" data-testid="task-delete">
            <Trash2 className="w-4 h-4" />
          </Button>
        </div>

        {task.description && (
          <p className="text-slate-700 whitespace-pre-wrap text-sm">{task.description}</p>
        )}

        {task.entity_label && (
          <Badge variant="outline" className="text-xs border-blue-200 text-blue-700">
            Lié à : {task.entity_type} · {task.entity_label}
          </Badge>
        )}

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 pt-2 border-t border-slate-100">
          <div>
            <label className="block text-xs uppercase tracking-wider text-slate-500 font-semibold mb-1">Statut</label>
            <select
              value={task.status}
              onChange={(e) => updateField('status', e.target.value)}
              disabled={savingField === 'status'}
              className="w-full h-9 rounded-md border border-slate-200 px-3 text-sm bg-white"
              data-testid="task-status-select"
            >
              {STATUS_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-xs uppercase tracking-wider text-slate-500 font-semibold mb-1">Priorité</label>
            <select
              value={task.priority}
              onChange={(e) => updateField('priority', e.target.value)}
              disabled={savingField === 'priority'}
              className="w-full h-9 rounded-md border border-slate-200 px-3 text-sm bg-white"
              data-testid="task-priority-select"
            >
              {PRIORITY_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-xs uppercase tracking-wider text-slate-500 font-semibold mb-1">Échéance</label>
            <input
              type="date"
              value={task.due_date || ''}
              onChange={(e) => updateField('due_date', e.target.value || null)}
              disabled={savingField === 'due_date'}
              className="w-full h-9 rounded-md border border-slate-200 px-3 text-sm bg-white"
              data-testid="task-due-input"
            />
          </div>
          <div className="sm:col-span-3">
            <label className="block text-xs uppercase tracking-wider text-slate-500 font-semibold mb-1">Assigné à</label>
            <select
              value={task.assignee_user_id || ''}
              onChange={(e) => updateField('assignee_user_id', e.target.value || null)}
              disabled={savingField === 'assignee_user_id'}
              className="w-full h-9 rounded-md border border-slate-200 px-3 text-sm bg-white"
              data-testid="task-assignee-select"
            >
              <option value="">— Personne —</option>
              {users.map((u) => (
                <option key={u.user_id} value={u.user_id}>{u.name || u.email}</option>
              ))}
            </select>
          </div>
        </div>

        {/* Pièces jointes de la tâche */}
        <div className="pt-3 border-t border-slate-100">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-sm font-semibold text-slate-900 flex items-center gap-2">
              <Paperclip className="w-4 h-4" /> Pièces jointes
            </h3>
            <input
              ref={fileRef}
              type="file"
              accept=".pdf,.png,.jpg,.jpeg,.webp,.heic,.heif"
              className="hidden"
              onChange={uploadTaskFile}
              data-testid="task-upload-input"
            />
            <Button
              variant="outline" size="sm"
              onClick={() => fileRef.current?.click()}
              disabled={uploading}
              data-testid="task-upload-btn"
            >
              {uploading
                ? <Loader2 key="up-load" className="w-3.5 h-3.5 mr-1 animate-spin" />
                : <Paperclip key="up-icon" className="w-3.5 h-3.5 mr-1" />}
              Joindre
            </Button>
          </div>
          {(!task.attachments || task.attachments.length === 0) && (
            <p className="text-xs text-slate-400">Aucune pièce jointe.</p>
          )}
          {task.attachments && task.attachments.length > 0 && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {task.attachments.map((a, i) => (
                <a
                  key={i}
                  href={fullUrl(a.url)}
                  target="_blank" rel="noreferrer"
                  className="flex items-center gap-2 p-2 rounded-md border border-slate-200 hover:bg-slate-50 text-sm text-slate-700"
                >
                  <FileText className="w-4 h-4 text-slate-400 shrink-0" />
                  <span className="truncate flex-1">{a.filename}</span>
                  <span className="text-xs text-slate-400">{Math.round((a.size || 0) / 1024)} ko</span>
                </a>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Fil de commentaires */}
      <div className="bg-white rounded-xl border border-slate-200 p-6 space-y-4" data-testid="task-comments">
        <h2 className="text-lg font-semibold text-slate-900 flex items-center gap-2">
          <ListTodo className="w-5 h-5" /> Discussion ({comments.length})
        </h2>

        {comments.length === 0 && (
          <p className="text-sm text-slate-500 text-center py-6">Aucun commentaire pour le moment.</p>
        )}

        <div className="space-y-3">
          {comments.map((c) => (
            <div key={c.comment_id} className="bg-slate-50 rounded-lg p-3 border border-slate-100" data-testid={`comment-${c.comment_id}`}>
              <div className="flex items-center justify-between text-xs text-slate-500 mb-1">
                <span className="font-medium text-slate-700">{c.author?.name || c.author?.email}</span>
                <span>{String(c.created_at || '').slice(0, 16).replace('T', ' ')}</span>
              </div>
              <p className="text-sm text-slate-900 whitespace-pre-wrap">{c.body}</p>
              {c.attachments && c.attachments.length > 0 && (
                <div className="mt-2 flex flex-wrap gap-2">
                  {c.attachments.map((a, i) => (
                    <a
                      key={i}
                      href={fullUrl(a.url)}
                      target="_blank" rel="noreferrer"
                      className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded bg-white border border-slate-200 hover:bg-slate-100"
                    >
                      <FileText className="w-3 h-3" /> {a.filename}
                    </a>
                  ))}
                </div>
              )}
              <div className="mt-2">
                <input
                  ref={(el) => { cmtFileRefs.current[c.comment_id] = el; }}
                  type="file"
                  accept=".pdf,.png,.jpg,.jpeg,.webp,.heic,.heif"
                  className="hidden"
                  onChange={(e) => uploadCmtFile(c.comment_id, e)}
                />
                <button
                  type="button"
                  onClick={() => cmtFileRefs.current[c.comment_id]?.click()}
                  className="text-xs text-blue-600 hover:text-blue-800 inline-flex items-center gap-1"
                >
                  <Paperclip className="w-3 h-3" /> Joindre un fichier
                </button>
              </div>
            </div>
          ))}
        </div>

        <div className="border-t border-slate-100 pt-3 flex items-end gap-2">
          <textarea
            value={newCmt}
            onChange={(e) => setNewCmt(e.target.value)}
            placeholder="Votre message…"
            rows={2}
            className="flex-1 rounded-md border border-slate-200 px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
            data-testid="new-comment-input"
            onKeyDown={(e) => {
              if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) postComment();
            }}
          />
          <Button onClick={postComment} disabled={posting || !newCmt.trim()} data-testid="new-comment-submit">
            {posting
              ? <Loader2 key="post-load" className="w-4 h-4 animate-spin" />
              : <Send key="post-icon" className="w-4 h-4" />}
          </Button>
        </div>
        <p className="text-[11px] text-slate-400 text-right">Ctrl/⌘ + Entrée pour envoyer</p>
      </div>
    </div>
  );
}
